import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowLeft, FaTrash } from 'react-icons/fa'; // Import FaTrash icon
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, doc, getDoc, getDocs, updateDoc, query, where, writeBatch, Timestamp } from 'firebase/firestore'; // Import query, where, writeBatch
import { useFireAuth } from '../FireAuthContext';
import DelayRender from './DelayRender';
import './AdminRaffle.css';

const AdminRaffle = () => {
  const { user } = useFireAuth();
  const [timestamp, setTimestamp] = useState('');
  const [adminPrizeItems, setAdminPrizeItems] = useState([]);
  const [newAdminPrizeItem, setNewAdminPrizeItem] = useState('');
  const [raffleParticipants, setRaffleParticipants] = useState([]);
  const navigate = useNavigate();

  const db = getFirestore(); // Get the Firestore instance

  const fetchRaffleData = useCallback(async () => {
    const raffleDocRef = doc(db, 'counters', 'raffle');
    const raffleDoc = await getDoc(raffleDocRef);
    if (raffleDoc.exists()) {
      const data = raffleDoc.data();
      const date = data.timestamp.toDate();
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      setTimestamp(localDate.toISOString().slice(0, 16));
      setAdminPrizeItems(data.prize);
    }

    const raffleCollectionRef = collection(db, 'raffle');
    const raffleSnapshot = await getDocs(raffleCollectionRef);
    const participants = {};
    raffleSnapshot.forEach((doc) => {
      const data = doc.data();
      if (participants[data.userId]) {
        participants[data.userId].ticketCount += 1;
      } else {
        participants[data.userId] = {
          userId: data.userId,
          ticketCount: 1,
        };
      }
    });

    const participantsArray = await Promise.all(
      Object.values(participants).map(async (participant) => {
        const userDocRef = doc(db, 'users', participant.userId);
        const userDoc = await getDoc(userDocRef);
        return {
          ...participant,
          userName: userDoc.exists() ? userDoc.data().name : 'Unknown User',
        };
      })
    );

    participantsArray.sort((a, b) => b.ticketCount - a.ticketCount);
    setRaffleParticipants(participantsArray);
  }, [db]);

  useEffect(() => {
    fetchRaffleData();
  }, [fetchRaffleData]);

  const handlePrizeItemChange = (e) => {
    setNewAdminPrizeItem(e.target.value);
  };

  const handleAddPrizeItem = () => {
    if (newAdminPrizeItem.trim() !== '') {
      setAdminPrizeItems((prevItems) => [...prevItems, newAdminPrizeItem.trim()]);
      setNewAdminPrizeItem('');
    }
  };

  const handleRemovePrizeItem = (item) => {
    setAdminPrizeItems((prevItems) => prevItems.filter((prizeItem) => prizeItem !== item));
  };

  const handleSave = async () => {
    const raffleDocRef = doc(db, 'counters', 'raffle');
    const updatedData = {
      timestamp: Timestamp.fromDate(new Date(timestamp)),
      prize: adminPrizeItems,
    };

    try {
      await updateDoc(raffleDocRef, updatedData);
      alert('Raffle settings updated successfully.');
    } catch (error) {
      console.error('Error updating raffle settings:', error);
      alert('Failed to update raffle settings.');
    }
  };

  const handleDeleteTickets = async (userId) => {
    const raffleCollectionRef = collection(db, 'raffle');
    const q = query(raffleCollectionRef, where('userId', '==', userId));
    const raffleSnapshot = await getDocs(q);

    const batch = writeBatch(db);
    raffleSnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    try {
      await batch.commit();
      alert('Tickets deleted successfully.');
      fetchRaffleData();
    } catch (error) {
      console.error('Error deleting tickets:', error);
      alert('Failed to delete tickets. Please try again later.');
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="admin-raffle-container">
      <FaArrowLeft className="admin-raffle-back-arrow" onClick={() => navigate(-1)} />
      <h2>Raffle Admin</h2>
      <div className="admin-raffle-form">
        <label htmlFor="timestamp">Raffle End Timestamp:</label>
        <input
          type="datetime-local"
          id="timestamp"
          value={timestamp}
          onChange={(e) => setTimestamp(e.target.value)}
        />
        <div className="admin-prize-items-list">
          <h3>Prize Items</h3>
          {adminPrizeItems.map((item, index) => (
            <div key={index} className="admin-prize-item">
              <p>{item}</p>
              <button onClick={() => handleRemovePrizeItem(item)}>Remove</button>
            </div>
          ))}
        </div>
        <input
          type="text"
          value={newAdminPrizeItem}
          onChange={handlePrizeItemChange}
          placeholder="Add new prize item"
        />
        <button className="admin-add-prize-button" onClick={handleAddPrizeItem}>Add Prize Item</button>
        <button className="admin-raffle-save-button" onClick={handleSave}>Save Settings</button>
      </div>
      <div className="admin-raffle-participants">
        <h3>Raffle Participants</h3>
        <ul>
          {raffleParticipants.map((participant, index) => (
            <li key={index}>
              {participant.userName} - {participant.ticketCount} {participant.ticketCount > 1 ? 'tickets' : 'ticket'}
              <FaTrash
                onClick={() => handleDeleteTickets(participant.userId)}
                className="delete-tickets-icon"
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default DelayRender(AdminRaffle, 300);
